import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";

/**
 * Small pill shown in the storefront header next to the cart / avatar
 * for logged-in customers whose tenant has loyalty enabled.
 *
 * Renders NOTHING when:
 *   - The user isn't logged in
 *   - The tenant's loyalty module is disabled
 *   - The API call fails (silently hidden — no user-facing error)
 *
 * Reads `/api/loyalty/me` once per mount + on user-id change.
 */
export default function LoyaltyPill() {
  const { user } = useAuth();
  const [data, setData] = useState(null);

  useEffect(() => {
    let alive = true;
    if (!user) { setData(null); return; }
    (async () => {
      try {
        const { data } = await api.get("/loyalty/me");
        if (!alive) return;
        if (data.enabled) setData(data);
      } catch { /* silently hide */ }
    })();
    return () => { alive = false; };
  }, [user?.id]);

  if (!data) return null;
  const label = data.labels?.coin_plural || "Coins";
  return (
    <Link
      to="/account/rewards"
      className="hidden sm:inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-amber-50 border border-amber-200 text-amber-800 text-xs font-medium hover:bg-amber-100 transition-colors"
      title={`${data.lifetime_earned} earned lifetime`}
      data-testid="loyalty-pill"
    >
      <span className="text-amber-500">★</span>
      <span data-testid="loyalty-balance">{data.balance}</span>
      <span className="opacity-70">{label}</span>
    </Link>
  );
}
